import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@maplix/forms';
import { ILabelOptions, IMapUploadVector, IStyleCondition, MapType } from '@maplix/utils';
import { clone } from 'lodash';
import { MapLayerService } from '@maplix/maps';
import { ImportLayers } from '../import-layers/import-layers';
import { IUploadLayerDataset } from '@maplix/cloud';
import { ApiService } from '@maplix/api';
import { ColorEvent } from 'ngx-color';
import { takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'maplix-import-upload-layers',
  templateUrl: './import-upload-layers.component.html',
  styleUrls: ['./import-upload-layers.component.scss'],
})
export class ImportUploadLayersComponent extends ImportLayers implements OnInit {
  @Input()
  public uploadLayer: FormGroup<IMapUploadVector>;

  @Input()
  public new: boolean;

  @Input()
  public themeColors: any;

  @Output()
  public openModal: EventEmitter<string> = new EventEmitter();

  public disabled: boolean;

  public mapType: MapType = MapType.MAP;

  public styleSources = [
    { label: 'Default', value: 'regular', icon: 'remixicon-palette-line' },
    { label: 'Heatmap', value: 'heatmap', icon: 'remixicon-contrast-2-fill' },
    { label: 'Clustered', value: 'cluster', icon: 'remixicon-focus-2-line' },
    { label: 'Rule based', value: 'rule-based', icon: 'remixicon-shuffle-line' },
  ];

  public allStyleSources = clone(this.styleSources);

  public visibleHelptexts: string[] = [];

  public selectedRule: FormGroup<IStyleCondition>;

  public selectedRuleView: 'style' | 'conditions';

  public get icon(): string {
    let result =
      this.uploadLayer.value.type === 'WMS'
        ? 'remixicon-server-fill'
        : this.uploadLayer.value.type === 'UPLOAD' && !this.uploadLayer.value.geomType
        ? 'remixicon-folder-upload-fill'
        : this.uploadLayer.value.geomType === 'Point'
        ? 'fas fa-map-marker-alt'
        : this.uploadLayer.value.geomType === 'LineString'
        ? 'fas fa-slash'
        : this.uploadLayer.value.geomType === 'Polygon'
        ? 'fas fa-vector-square'
        : 'remixicon-map-2-line';

    result += ' mr-2';
    return result;
  }

  public labelForm = new FormGroup<ILabelOptions>({
    field: new FormControl<string>('NO_LABELS'),
    alignment: new FormControl('center'),
    textColor: new FormControl('#243039'),
    showHalo: new FormControl(true),
    haloColor: new FormControl('#FFFFFF'),
    offsetX: new FormControl(0),
    offsetY: new FormControl(15),
    fontSize: new FormControl(12),
    minZoom: new FormControl(2),
    maxZoom: new FormControl(22),
  });

  constructor(@Inject('environment') environment: any, public mapService: MapLayerService) {
    super(environment, mapService);
  }

  ngOnInit() {
    super.ngOnInit();

    this.setStyleSources();

    this.labelForm.patchValue(this.uploadLayer.getRawValue().styleConditions[0]?.label);

    this.labelForm.valueChanges
      .pipe(
        tap((labelOpts) => {
          (this.uploadLayer.getControl('styleConditions') as FormArray<IStyleCondition>).controls.forEach(
            (sc: FormGroup<IStyleCondition>) => {
              const labelControl = sc.getFormGroup('label');
              labelControl.patchValue(labelOpts);
              labelControl.markAsDirty();
            }
          );

          // trigger change
          setTimeout(() =>
            this.uploadLayer
              .getControl('styleConditions')
              .setValue(this.uploadLayer.getControl('styleConditions').value)
          );
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  private setStyleSources() {
    const geomType = this.uploadLayer.getControl('geomType').value;

    if (geomType === 'Point') {
      this.styleSources = clone(this.allStyleSources);
    } else {
      this.styleSources = clone(this.allStyleSources).filter(
        (source) => source.value !== 'cluster' && source.value !== 'heatmap'
      );
    }
  }

  public onToggleHelptext(layer: string) {
    if (this.visibleHelptexts.includes(layer)) {
      this.visibleHelptexts = this.visibleHelptexts.filter((id) => id !== layer);
    } else {
      this.visibleHelptexts.push(layer);
    }
  }

  public onEditRule(styleCondition: FormGroup<IStyleCondition>): void {
    this.selectedRule = styleCondition;
    this.selectedRuleView = 'style';
    styleCondition.markAsDirty();
  }

  public onEditConditions(styleCondition: FormGroup<IStyleCondition>): void {
    this.selectedRule = styleCondition;
    this.selectedRuleView = 'conditions';
    styleCondition.markAsDirty();
  }

  public onRemoveRule(array: FormArray<IStyleCondition>, styleCondition: FormGroup<IStyleCondition>) {
    const index = array.controls.findIndex(
      (ctrl: FormGroup<IStyleCondition>) => ctrl.getControl('id').value === styleCondition.getControl('id').value
    );
    array.removeAt(index);
    array.markArrayAsChanged();
  }

  public onChangeTab() {
    this.selectedRule = undefined;
  }

  public onAddRule(array: FormArray<IStyleCondition>): void {
    const newRule = this.mapService.getDefaultStyling();
    array.push(newRule);
    array.markArrayAsChanged();
  }

  public onOpenDatasetModal() {
    this.openModal.emit(this.uploadLayer.getRawValue()._id);
  }
}
